import { useState } from 'react';
import { resetStart, resetFinish, clearToken, saveToken, announceSignedIn } from './api.js';
import { go } from './nav.js';
import { returnParam } from './gate.jsx';
import { problem } from './password.js';
import { PwChecklist } from './pwcheck.jsx';
import { Eye } from './Eye.jsx';
import { PhoneRow, joinPhone, deviceDial, maskPhone, useChannels } from './phone.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * FORGOT YOUR PASSWORD — THE OTHER DEAD BUTTON
 *
 * ⚠️ IT PRINTED "Not built yet — the reset screen is next." UNTIL TODAY.
 * `otp_codes.purpose = 'reset_password'` has been in the schema since
 * migration 002 and was issued by nothing until 16 September; the two routes
 * have existed since then with no screen on the end of them.
 *
 * Copy from `04_Email_SignUp.html`'s reset block: "New password", "Confirm new
 * password", "Choose a new password", "Type it again", "Save new password",
 * "Didn't get it?", "Send it again".
 *
 * ── THE SAME ANSWER EITHER WAY, AND THE SCREEN MUST NOT IMPROVE ON IT ───────
 *
 * ⚠️ `/reset/start` ANSWERS 202 WHETHER OR NOT THE ACCOUNT EXISTS. That is
 * deliberate: it is what stops a stranger typing addresses at Eleat to learn
 * who is on it. A screen that says "we've sent you a code" only for real
 * accounts, or that shows "no account found", hands back exactly the thing the
 * route refuses to give — and it would look like helpfulness, which is why it
 * is written down here rather than left to whoever edits this next.
 *
 * So this screen always moves to step 2, always says the same sentence, and
 * learns nothing it can leak.
 *
 * ── A CORRECT CODE ENDS EVERY OLD SESSION AND STARTS THIS ONE ─────────────
 *
 * ⚠️ `/reset/finish` bumps `token_version` (migration 087), which ends every
 * session already open — including one on a stolen device, the commonest real
 * reason somebody resets a password. The token held on THIS device was cut
 * before the bump, so it is cleared.
 *
 * ⚠️ AND SINCE 9 OCT 2026 THE ROUTE HANDS BACK A NEW TOKEN, minted after the
 * bump, the way a password change always has (setPassword). Al-Noor: "I had to
 * go back to sign in… aren't those too many steps". So a correct code and a
 * new password open Home, signed in. Step 3 ("Password saved" + Sign in) stays
 * only for a server that sends no token.
 * ══════════════════════════════════════════════════════════════════════════ */

export default function Reset() {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState('');
  /* ⚠️ BY NUMBER — `?reset=1&by=whatsapp`, from the WhatsApp sign-in (8 Oct
     2026): the code goes to WhatsApp. Only while WhatsApp codes are on. */
  const { whatsapp: waOn } = useChannels();
  const byWa = waOn && new URLSearchParams(window.location.search).get('by') === 'whatsapp';
  const [dial, setDial] = useState(deviceDial);
  const [num, setNum] = useState('');
  const identifier = byWa ? joinPhone(dial, num) : typed;
  const signinHref = byWa ? '?signin=1&by=whatsapp' : '?signin=1';
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resent, setResent] = useState('');

  const pwProblem = problem(pw, pw2);
  /* the button is never greyed out (Al-Noor, 10 Oct 2026) — pressing it says
     what is missing */
  const [tried, setTried] = useState(false);

  async function start(e) {
    e.preventDefault();
    if (busy) return;
    /* never greyed out (Al-Noor, 10 Oct 2026): pressed too early it says what is missing */
    if (identifier.trim() === '') { setError({ text: byWa ? 'Enter your WhatsApp number.' : 'Enter your email address.' }); return; }
    setBusy(true); setError(null);
    try {
      const d = await resetStart({ identifier });
      if (d?.dev_code) setCode(String(d.dev_code));
      setStep(2);
    } catch (err) {
      /* ⚠️ A RATE LIMIT AND A NETWORK FAILURE ARE REAL AND ARE SHOWN. What is
         never shown is "no such account" — the route does not say it, and
         nothing here may infer it. `/reset/start` allows three a minute. */
      setError({ text: err.message, offline: !!err.offline });
    } finally { setBusy(false); }
  }

  async function resend() {
    if (busy) return;
    setBusy(true); setError(null);
    try { await resetStart({ identifier }); setResent('Sent again.'); }
    catch (err) { setError({ text: err.message, offline: !!err.offline }); }
    finally { setBusy(false); }
  }

  async function finish(e) {
    e.preventDefault();
    if (busy) return;
    if (code.trim().length < 4 || pwProblem !== null) {
      setTried(true);
      setError({ text: code.trim().length < 4 ? 'Enter the code we sent you.' : pwProblem });
      return;
    }
    setBusy(true); setError(null);
    try {
      const d = await resetFinish({ identifier, code, new_password: pw, confirm_password: pw2 });
      setPw(''); setPw2('');
      /* ⚠️ EVERY OLD SESSION IS OVER — see the header. */
      clearToken();
      /* ⚠️ AND THIS ONE STARTS AT ONCE — Al-Noor, 9 Oct 2026: "I had to go
         back to sign in… aren't those too many steps". The server hands a
         token minted AFTER the bump (finishPasswordReset), so the new
         password signs them straight in and Home opens. A server without it
         still shows "Password saved" and the Sign in button. */
      if (d && d.token) {
        saveToken(d.token, true);
        announceSignedIn();
        go(returnParam(), { replace: true });
        return;
      }
      setStep(3);
    } catch (err) {
      setError({ text: err.message, offline: !!err.offline });
    } finally { setBusy(false); }
  }

  if (step === 3) {
    return (
      <div className="screen">
        <div className="logo">
          <img className="lmark" src="/eleat_mark.png" alt="" />
          <img className="lword" src="/eleat_word.png" alt="Eleat" />
        </div>
        <main>
          {/* ⚠️ IT SAYS WHAT ACTUALLY HAPPENED, INCLUDING THE PART THAT LOOKS
              LIKE BAD NEWS. Somebody signed in on a tablet who finds
              themselves signed out needs to know why, or they will think Eleat
              broke. Migration 087 is what makes this sentence true. */}
          <h1>Password saved</h1>
          <p className="sub">
            You have been signed out everywhere. Sign in with your new password.
          </p>
          <a className="cta" href={signinHref}>Sign in</a>
        </main>
      </div>
    );
  }

  if (step === 2) {
    return (
      <form className="screen" onSubmit={finish} noValidate>
        <div className="top">
          <button className="back" type="button" aria-label="Back"
            onClick={() => { setStep(1); setError(null); }}>‹</button>
        </div>
        <div className="logo">
          <img className="lmark" src="/eleat_mark.png" alt="" />
          <img className="lword" src="/eleat_word.png" alt="Eleat" />
        </div>
        <main>
          <h1>Enter the 6-digit code</h1>
          {/* ⚠️ "If there is an account" — NOT "we sent it to you". The route
              refuses to say whether the account exists and this sentence keeps
              that promise instead of quietly breaking it. */}
          <p className="sub">If there is an Eleat account for <b>{byWa ? maskPhone(identifier) : identifier.trim()}</b>, a code is on its way.</p>

          <input className="tin otp1" type="text" inputMode="numeric"
            autoComplete="one-time-code" maxLength={6}
            aria-label="Enter the 6-digit code"
            value={code}
            onChange={(ev) => setCode(ev.target.value.replace(/\D/g, ''))} />

          <div className="resend">
            <span className="mut">Didn’t get it?</span>{' '}
            <button className="link" type="button" onClick={resend}>
              {byWa ? 'Resend on WhatsApp' : 'Send it again'}
            </button>
          </div>
          {resent && <div className="mut" aria-live="polite">{resent}</div>}

          <div className="label" style={{ marginTop: 16 }}>New password</div>
          <div className="pwwrap">
            <input className="tin" type={showPw ? 'text' : 'password'}
              autoComplete="new-password" placeholder="Choose a new password"
              aria-label="New password"
              value={pw} onChange={(e) => setPw(e.target.value)} />
            <Eye on={showPw} onToggle={() => setShowPw((v) => !v)} />
          </div>
          <PwChecklist pw={pw} />

          <div className="label" style={{ marginTop: 16 }}>Confirm new password</div>
          <div className="pwwrap">
            <input className="tin" type={showPw2 ? 'text' : 'password'}
              autoComplete="new-password" placeholder="Type it again"
              aria-label="Confirm new password"
              value={pw2} onChange={(e) => setPw2(e.target.value)} />
            <Eye on={showPw2} onToggle={() => setShowPw2((v) => !v)} />
          </div>
          <div className="pwhint" aria-live="polite">
            {pw2 && pw !== pw2 ? 'Those two passwords are not the same.' : ''}
          </div>

          <div className="err" data-offline={error?.offline ? 'true' : 'false'} aria-live="polite">
            {error?.text || ''}
          </div>
        </main>
        <footer>
          <button className="cta" type="submit"
            >
            Save new password
          </button>
          {byWa
            ? <div className="deliv">Standard WhatsApp message rates may apply.</div>
            : <div className="deliv">Arrives in seconds · check your spam folder if it doesn’t</div>}
        </footer>
      </form>
    );
  }

  return (
    <form className="screen" onSubmit={start} noValidate>
      <div className="top">
        <a className="back" href={signinHref} aria-label="Back">‹</a>
      </div>
      <div className="logo">
        <img className="lmark" src="/eleat_mark.png" alt="" />
        <img className="lword" src="/eleat_word.png" alt="Eleat" />
      </div>
      <main>
        <h1>Forgot your password?</h1>
        {/* ⚠️ THIS DOOR TAKES EITHER, AND THAT IS NOT THE CONTRADICTION IT
            LOOKS LIKE. Sign-in is two doors — WhatsApp and Email, Al, 16
            September — because each is a way IN and each asks for its own
            thing. A reset is not a way in; it is one route that finds an
            account by number or address (`resetStart` refines on
            `phone || email`) and sends a code to whichever was given. Asking
            twice, on two screens, for a thing somebody has forgotten the
            password to would be two screens where one is honest. */}
        {byWa ? (<>
          <div className="label">Mobile number</div>
          <PhoneRow dial={dial} number={num} onDial={setDial} onNumber={setNum} />
        </>) : (<>
          <p className="sub">Enter your email address.</p>
          <input className="tin" type="text" inputMode="email" autoComplete="username"
            autoCapitalize="off" spellCheck={false} placeholder="you@example.com"
            aria-label="Email address"
            value={typed} onChange={(e) => setTyped(e.target.value)} />
        </>)}

        <div className="err" data-offline={error?.offline ? 'true' : 'false'} aria-live="polite">
          {error?.text || ''}
        </div>
      </main>
      <footer>
        <button className="cta" type="submit">
          Send my code
        </button>
        <a className="forgot" href={signinHref}>Back to sign in</a>
      </footer>
    </form>
  );
}
